import React from "react";
import { useState, useMemo } from "react";

const CATEGORIES = [
  { key: "feature", label: "Nova Funcionalidade", color: "#3B82F6", bg: "#1e3a5f", icon: "⬡" },
  { key: "improvement", label: "Melhoria", color: "#F59E0B", bg: "#3d2e0a", icon: "↑" },
  { key: "bug", label: "Correção de Bug", color: "#EF4444", bg: "#3d1010", icon: "✕" },
  { key: "rework", label: "Retrabalho", color: "#F97316", bg: "#3d1e0a", icon: "↺" },
  { key: "support", label: "Suporte", color: "#8B5CF6", bg: "#2a1a4d", icon: "◈" },
];

const STATUSES = [
  { key: "todo", label: "A Fazer", dot: "#6B7280" },
  { key: "inprogress", label: "Em Andamento", dot: "#3B82F6" },
  { key: "done", label: "Concluída", dot: "#10B981" },
  { key: "blocked", label: "Bloqueada", dot: "#EF4444" },
];

const EMPTY_FORM = {
  title: "",
  category: "feature",
  status: "todo",
  project: "",
  estimate: "",
  note: "",
};

function getWeekLabel() {
  const now = new Date();
  const day = now.getDay();
  const diffMon = (day === 0 ? -6 : 1 - day);
  const mon = new Date(now); mon.setDate(now.getDate() + diffMon);
  const fri = new Date(mon); fri.setDate(mon.getDate() + 4);
  const fmt = (d) => d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });
  return `${fmt(mon)} – ${fmt(fri)}`;
}

export default function DevWeeklyBoard() {
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterCat, setFilterCat] = useState("all");

  const summary = useMemo(() => {
    const total = tasks.length;
    const byCategory = {};
    CATEGORIES.forEach(c => { byCategory[c.key] = tasks.filter(t => t.category === c.key).length; });
    const done = tasks.filter(t => t.status === "done").length;
    const blocked = tasks.filter(t => t.status === "blocked").length;
    return { total, byCategory, done, blocked };
  }, [tasks]);

  const filtered = useMemo(() => tasks.filter(t => {
    if (filterStatus !== "all" && t.status !== filterStatus) return false;
    if (filterCat !== "all" && t.category !== filterCat) return false;
    return true;
  }), [tasks, filterStatus, filterCat]);

  const handleSubmit = () => {
    if (!form.title.trim()) return;
    if (editId !== null) {
      setTasks(tasks.map(t => t.id === editId ? { ...form, id: editId } : t));
      setEditId(null);
    } else {
      setTasks([...tasks, { ...form, id: Date.now() }]);
    }
    setForm(EMPTY_FORM);
    setShowForm(false);
  };

  const handleEdit = (task) => {
    setForm({ title: task.title, category: task.category, status: task.status, project: task.project, estimate: task.estimate, note: task.note });
    setEditId(task.id);
    setShowForm(true);
  };

  const handleDelete = (id) => setTasks(tasks.filter(t => t.id !== id));

  const cycleStatus = (task) => {
    const keys = STATUSES.map(s => s.key);
    const next = keys[(keys.indexOf(task.status) + 1) % keys.length];
    setTasks(tasks.map(t => t.id === task.id ? { ...t, status: next } : t));
  };

  const cat = (key) => CATEGORIES.find(c => c.key === key);
  const sta = (key) => STATUSES.find(s => s.key === key);

  return (
    <div style={{ paddingTop: "200px", minHeight: "100vh", background: "#0d0d0f", color: "#e8e6e0", fontFamily: "'Courier New', Courier, monospace", paddingLeft: "1.5rem", paddingRight: "1.5rem", paddingBottom: "2rem"  }}>
      <style>{`
        * { box-sizing: border-box; }
        ::placeholder { color: #555; }
        input, select, textarea { outline: none; }
        .task-card:hover { border-color: #333 !important; }
        .btn-ghost:hover { background: #1a1a1f !important; }
        .chip { display:inline-flex; align-items:center; gap:4px; padding:2px 8px; border-radius:3px; font-size:11px; font-weight:600; letter-spacing:.05em; text-transform:uppercase; }
        @keyframes fadeIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
        .fade-in { animation: fadeIn .2s ease; }
        .progress-bar { height:3px; background:#1a1a1f; border-radius:2px; overflow:hidden; }
        .progress-fill { height:100%; background:linear-gradient(90deg,#3B82F6,#10B981); transition:width .4s ease; border-radius:2px; }
      `}</style>

      {/* Header */}
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "2.5rem" }}>
          <div>
            <div style={{ fontSize: 11, letterSpacing: ".2em", color: "#555", textTransform: "uppercase", marginBottom: 6 }}>Dev Weekly Board</div>
            <h1 style={{ margin: 0, fontSize: "1.8rem", fontWeight: 700, letterSpacing: "-.02em", color: "#f0ede6" }}>Semana de Trabalho</h1>
            <div style={{ color: "#555", fontSize: 13, marginTop: 4 }}>{getWeekLabel()}</div>
          </div>
          <button onClick={() => { setShowForm(!showForm); setEditId(null); setForm(EMPTY_FORM); }}
            style={{ background: showForm ? "#1a1a1f" : "#f0ede6", color: showForm ? "#888" : "#0d0d0f", border: "none", padding: "10px 20px", borderRadius: 4, fontSize: 13, fontWeight: 700, cursor: "pointer", letterSpacing: ".05em", fontFamily: "inherit", transition: "all .15s" }}>
            {showForm ? "✕ Cancelar" : "+ Nova Tarefa"}
          </button>
        </div>

        {/* Summary */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 12, marginBottom: "2rem" }}>
          <div style={{ background: "#111114", border: "1px solid #1e1e24", borderRadius: 6, padding: "14px 16px" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#f0ede6" }}>{summary.total}</div>
            <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: ".1em" }}>Total</div>
          </div>
          {CATEGORIES.map(c => (
            <div key={c.key} style={{ background: "#111114", border: `1px solid ${summary.byCategory[c.key] > 0 ? c.color + "33" : "#1e1e24"}`, borderRadius: 6, padding: "14px 16px" }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: summary.byCategory[c.key] > 0 ? c.color : "#333" }}>{summary.byCategory[c.key]}</div>
              <div style={{ fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: ".08em", lineHeight: 1.4 }}>{c.label}</div>
            </div>
          ))}
          <div style={{ background: "#111114", border: "1px solid #1e1e24", borderRadius: 6, padding: "14px 16px" }}>
            <div style={{ fontSize: 28, fontWeight: 700, color: "#EF4444" }}>{summary.blocked}</div>
            <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: ".1em" }}>Bloqueadas</div>
          </div>
        </div>

        {/* Progress bar */}
        {summary.total > 0 && (
          <div style={{ marginBottom: "2rem" }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#555", marginBottom: 6 }}>
              <span>Progresso da semana</span>
              <span style={{ color: "#10B981" }}>{summary.done}/{summary.total} concluídas</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${summary.total ? (summary.done / summary.total) * 100 : 0}%` }} />
            </div>
          </div>
        )}

        {/* Form */}
        {showForm && (
          <div className="fade-in" style={{ background: "#111114", border: "1px solid #222228", borderRadius: 8, padding: "1.5rem", marginBottom: "2rem" }}>
            <div style={{ fontSize: 11, color: "#555", textTransform: "uppercase", letterSpacing: ".15em", marginBottom: "1.2rem" }}>
              {editId ? "Editar Tarefa" : "Nova Tarefa"}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="Título da tarefa *" style={inputStyle} />
              <input value={form.project} onChange={e => setForm({ ...form, project: e.target.value })}
                placeholder="Ferramenta / Projeto" style={inputStyle} />
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={inputStyle}>
                {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.icon} {c.label}</option>)}
              </select>
              <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })} style={inputStyle}>
                {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
              <input value={form.estimate} onChange={e => setForm({ ...form, estimate: e.target.value })}
                placeholder="Estimativa (ex: 2h, 1 dia)" style={inputStyle} />
              <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })}
                placeholder="Observação (ex: refeita por mudança de requisito...)" rows={2}
                style={{ ...inputStyle, resize: "vertical", gridColumn: "1 / -1" }} />
            </div>
            <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
              <button onClick={handleSubmit} style={{ background: "#f0ede6", color: "#0d0d0f", border: "none", padding: "9px 20px", borderRadius: 4, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", letterSpacing: ".05em" }}>
                {editId ? "Salvar Alterações" : "Adicionar Tarefa"}
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        {tasks.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginBottom: "1.5rem", flexWrap: "wrap" }}>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ ...inputStyle, padding: "6px 10px", fontSize: 11 }}>
              <option value="all">Todos os status</option>
              {STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <select value={filterCat} onChange={e => setFilterCat(e.target.value)} style={{ ...inputStyle, padding: "6px 10px", fontSize: 11 }}>
              <option value="all">Todas as categorias</option>
              {CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
        )}

        {/* Task list */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "3rem", color: "#333", fontSize: 13, border: "1px dashed #1e1e24", borderRadius: 8 }}>
              {tasks.length === 0 ? "Nenhuma tarefa ainda. Adicione a primeira tarefa da semana." : "Nenhuma tarefa encontrada com esses filtros."}
            </div>
          )}
          {filtered.map(task => {
            const c = cat(task.category);
            const s = sta(task.status);
            return (
              <div key={task.id} className="task-card" style={{ background: "#111114", border: "1px solid #1a1a1f", borderRadius: 6, padding: "14px 16px", display: "flex", alignItems: "flex-start", gap: 14, transition: "border-color .15s" }}>
                {/* Category bar */}
                <div style={{ width: 3, minHeight: 40, borderRadius: 2, background: c.color, flexShrink: 0, marginTop: 2 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: task.status === "done" ? "#555" : "#e8e6e0", textDecoration: task.status === "done" ? "line-through" : "none" }}>{task.title}</span>
                    {task.project && <span style={{ fontSize: 10, color: "#444", background: "#1a1a1f", padding: "2px 7px", borderRadius: 3 }}>{task.project}</span>}
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    <span className="chip" style={{ background: c.bg, color: c.color }}>{c.icon} {c.label}</span>
                    <button onClick={() => cycleStatus(task)} className="chip btn-ghost" style={{ background: "#1a1a1f", color: s.dot, border: "none", cursor: "pointer", fontFamily: "inherit" }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.dot, display: "inline-block" }} />
                      {s.label}
                    </button>
                    {task.estimate && <span style={{ fontSize: 10, color: "#555" }}>⏱ {task.estimate}</span>}
                  </div>
                  {task.note && <div style={{ fontSize: 11, color: "#555", marginTop: 6, fontStyle: "italic", borderLeft: "2px solid #222", paddingLeft: 8 }}>{task.note}</div>}
                </div>
                <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                  <button onClick={() => handleEdit(task)} className="btn-ghost" style={{ background: "transparent", border: "none", color: "#555", cursor: "pointer", padding: "4px 8px", borderRadius: 3, fontSize: 12 }}>✎</button>
                  <button onClick={() => handleDelete(task.id)} className="btn-ghost" style={{ background: "transparent", border: "none", color: "#555", cursor: "pointer", padding: "4px 8px", borderRadius: 3, fontSize: 12 }}>✕</button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        {summary.byCategory["rework"] > 0 && (
          <div style={{ marginTop: "2rem", padding: "12px 16px", background: "#1a0f00", border: "1px solid #F9731622", borderRadius: 6, fontSize: 12, color: "#F97316" }}>
            ↺ <strong>{summary.byCategory["rework"]}</strong> tarefa{summary.byCategory["rework"] > 1 ? "s são retrabalho" : " é retrabalho"} — decorrente{summary.byCategory["rework"] > 1 ? "s" : ""} de mudanças de requisito.
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle = {
  background: "#0d0d0f",
  border: "1px solid #222228",
  borderRadius: 4,
  color: "#e8e6e0",
  padding: "9px 12px",
  fontSize: 13,
  fontFamily: "'Courier New', Courier, monospace",
  width: "100%",
};
